import { format } from 'date-fns';

import type {
  GiftPassBookingRef,
  GiftPassEventRow,
  GiftPassEventType,
  GiftPassListRow,
  GiftPassSendRow,
  GiftPassSource,
  GiftPassSourceFilter,
  GiftPassStatus,
  GiftPassStatusFilter,
} from '../../api/gift-passes';
import { formatVenueDate } from '../../utils/venueTime';

export const GIFT_PASS_LIST_PATH = '/admin/gift-passes';

export function giftPassPath(id: string): string {
  return `${GIFT_PASS_LIST_PATH}/${id}`;
}

export function maskedPassCode(codeMasked: string): string {
  return `•••• ${codeMasked}`;
}

export const GIFT_PASS_STATUS_LABEL: Record<GiftPassStatus, string> = {
  ISSUED: 'Issued',
  SENT: 'Sent',
  REDEEMED: 'Redeemed',
  VOIDED: 'Voided',
  EXPIRED: 'Expired',
};

export const GIFT_PASS_SOURCE_LABEL: Record<GiftPassSource, string> = {
  PURCHASE: 'Purchase',
  REFUND: 'Refund',
  COMP: 'Comp',
};

function optionsFrom<T extends string>(labels: Record<T, string>) {
  return [
    { value: 'all' as const, label: 'All' },
    ...(Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] })),
  ];
}

export const GIFT_PASS_STATUS_OPTIONS: { value: GiftPassStatusFilter; label: string }[] =
  optionsFrom(GIFT_PASS_STATUS_LABEL);

export const GIFT_PASS_SOURCE_OPTIONS: { value: GiftPassSourceFilter; label: string }[] =
  optionsFrom(GIFT_PASS_SOURCE_LABEL);

export function originDetail(row: GiftPassListRow): string | null {
  if (row.source === 'REFUND' && row.refundedBookingReference) {
    return `from ${row.refundedBookingReference}`;
  }
  if (row.source === 'COMP') return row.compReason;
  return null;
}

export interface GiftPassListQuery {
  search: string;
  status: GiftPassStatusFilter;
  source: GiftPassSourceFilter;
}

export function isGiftPassFiltered(query: GiftPassListQuery): boolean {
  return query.search.trim() !== '' || query.status !== 'all' || query.source !== 'all';
}

const SEARCH_HINT = 'Try an email, a name, the last characters of the code, or a booking code.';

export function giftPassEmptyCopy(search: string, filtered: boolean) {
  const trimmed = search.trim();
  if (trimmed !== '') return { title: `No gift passes match “${trimmed}”`, body: SEARCH_HINT };
  if (filtered) return { title: 'No gift passes match these filters', body: SEARCH_HINT };
  return {
    title: 'No gift passes yet',
    body: 'Purchased, refunded and comped passes will appear here.',
  };
}

export function giftPassCountLabel(total: number): string {
  return `${total} ${total === 1 ? 'gift pass' : 'gift passes'}`;
}

export function formatPassDate(iso: string): string {
  return format(new Date(iso), 'MMM d, yyyy');
}

export function formatPassDateTime(iso: string): string {
  return format(new Date(iso), "MMM d, yyyy '·' h:mm aaa");
}

/** Icon key rather than a component, so this module stays free of React. */
export type GiftPassEventIcon = 'plus' | 'mail' | 'ticket' | 'ban' | 'repeat';

const ISSUED_LABEL: Record<GiftPassSource, string> = {
  PURCHASE: 'Purchased',
  REFUND: 'Issued from a refund',
  COMP: 'Issued as a comp',
};

export function eventHeadline(
  event: GiftPassEventRow,
  source: GiftPassSource,
): { label: string; icon: GiftPassEventIcon } {
  const to = event.recipientEmail ? ` to ${event.recipientEmail}` : '';
  const by = event.actorName ? ` by ${event.actorName}` : '';
  const headlines: Record<GiftPassEventType, { label: string; icon: GiftPassEventIcon }> = {
    ISSUED: { label: ISSUED_LABEL[source], icon: 'plus' },
    SENT: { label: `Sent${to}`, icon: 'mail' },
    REDEEMED: { label: 'Redeemed on booking', icon: 'ticket' },
    VOIDED: { label: `Voided${by}`, icon: 'ban' },
    RESENT: { label: `Email sent again${to}${by}`, icon: 'repeat' },
  };
  return headlines[event.type];
}

export function eventCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'event' : 'events'} · newest first`;
}

export type SendState = 'sent' | 'scheduled' | 'cancelled';

export function sendState(send: GiftPassSendRow): SendState {
  if (send.sentAt) return 'sent';
  if (send.cancelledAt) return 'cancelled';
  return 'scheduled';
}

export const SEND_STATE_LABEL: Record<SendState, string> = {
  sent: 'Sent',
  scheduled: 'Scheduled',
  cancelled: 'Cancelled',
};

export function orderSummary(order: { bundleName: string | null; passCount: number }): string {
  if (!order.bundleName) return order.passCount === 1 ? 'Single pass' : `${order.passCount} passes`;
  return `${order.bundleName} · ${order.passCount} passes`;
}

/** Read from the venue's own calendar, never the viewer's. */
export function bookingWorkshopLine(booking: GiftPassBookingRef): string {
  return [
    booking.workshopName,
    booking.venueName,
    formatVenueDate(booking.localStartsAt, 'MMM d, yyyy'),
  ]
    .filter(Boolean)
    .join(' · ');
}
